import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './use-auth';
import { LoginForm } from './login-form';
import { NewPasswordForm } from './new-password-form';
import { ForgotPasswordForm } from './forgot-password-form';
import { PostEditor } from './post-editor';
import type { NewPasswordChallenge } from '../../components/shared/auth/cognito';
import { listAdminPosts, getAdminPost, deletePost } from '../../components/shared/blog/blog-api';
import { formatDate } from '../../components/shared/blog/format-date';
import { isAdminConfigured, bypassAdminAuth } from '../../config';
import type { Post, PostSummary } from '../../components/shared/blog/contracts/post';

type View = { mode: 'list' } | { mode: 'edit'; post: Post | null };

export function AdminPage() {
  const auth = useAuth();

  useEffect(() => {
    document.title = 'Admin — Nick Gardner';
  }, []);

  if (!isAdminConfigured() && !bypassAdminAuth()) {
    return (
      <main id="main" className="mx-auto max-w-content px-5 py-24 text-center">
        <p className="section-kicker">{'// admin'}</p>
        <p className="mt-3 text-muted">
          Admin isn&apos;t configured in this build (no Cognito settings).
        </p>
      </main>
    );
  }

  return (
    <main id="main" className="mx-auto max-w-content px-5 py-16">
      {auth.status === 'checking' && (
        <p className="text-center font-mono text-sm text-muted">checking session…</p>
      )}

      {auth.status === 'signed-out' && (
        <>
          <div className="mb-8 text-center">
            <p className="section-kicker">{'// admin'}</p>
            <h1 className="mt-2 font-mono text-2xl font-bold text-fg">Blog admin</h1>
          </div>
          <SignedOut auth={auth} />
        </>
      )}

      {auth.status === 'signed-in' && (
        <AdminDashboard email={auth.email} getToken={auth.getIdToken} onSignOut={auth.signOut} />
      )}
    </main>
  );
}

type SignedOutView =
  | { mode: 'signin' }
  | { mode: 'new-password'; challenge: NewPasswordChallenge }
  | { mode: 'forgot' };

/**
 * Signed-out state machine: the sign-in card, plus the first-login forced-reset
 * card and the forgot-password flow it can branch into. Only one card shows at a
 * time. Completing new-password / a normal sign-in flips useAuth to signed-in,
 * which unmounts this whole subtree.
 */
function SignedOut({ auth }: { auth: ReturnType<typeof useAuth> }) {
  const [view, setView] = useState<SignedOutView>({ mode: 'signin' });
  // A one-shot confirmation shown on the sign-in card (e.g. after a reset).
  const [notice, setNotice] = useState<string | null>(null);

  const backToSignIn = (message?: string) => {
    setNotice(message ?? null);
    setView({ mode: 'signin' });
  };

  if (view.mode === 'new-password') {
    return (
      <NewPasswordForm
        onSubmit={(pw) => auth.completeNewPassword(view.challenge, pw)}
        onCancel={() => backToSignIn()}
      />
    );
  }

  if (view.mode === 'forgot') {
    return (
      <ForgotPasswordForm
        onRequestCode={auth.forgotPassword}
        onConfirm={auth.confirmForgotPassword}
        onDone={backToSignIn}
      />
    );
  }

  return (
    <LoginForm
      notice={notice}
      onSignIn={auth.signIn}
      onNewPasswordRequired={(challenge) => setView({ mode: 'new-password', challenge })}
      onForgotPassword={() => {
        setNotice(null);
        setView({ mode: 'forgot' });
      }}
    />
  );
}

interface DashboardProps {
  email?: string;
  getToken: () => Promise<string>;
  onSignOut: () => void;
}

function AdminDashboard({ email, getToken, onSignOut }: DashboardProps) {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await listAdminPosts(token); // no status = drafts + published
      setPosts(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function openEdit(slug: string) {
    setError(null);
    try {
      const token = await getToken();
      const post = await getAdminPost(token, slug);
      setView({ mode: 'edit', post });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open post.');
    }
  }

  async function handleDelete(slug: string) {
    if (!window.confirm(`Delete "${slug}"? This can't be undone.`)) return;
    setBusySlug(slug);
    try {
      const token = await getToken();
      await deletePost(token, slug);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusySlug(null);
    }
  }

  if (view.mode === 'edit') {
    return (
      <PostEditor
        initial={view.post}
        getToken={getToken}
        onSaved={async () => {
          setView({ mode: 'list' });
          await refresh();
        }}
        onCancel={() => setView({ mode: 'list' })}
      />
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-kicker">{'// admin'}</p>
          <h1 className="mt-1 font-mono text-2xl font-bold text-fg">Posts</h1>
        </div>
        <div className="flex items-center gap-4 font-mono text-sm">
          {email && <span className="text-muted">{email}</span>}
          <button onClick={onSignOut} className="text-muted hover:text-accent">
            sign out
          </button>
          <button
            onClick={() => setView({ mode: 'edit', post: null })}
            className="rounded-md bg-accent px-3 py-1.5 font-medium text-bg hover:opacity-90"
          >
            + new
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 font-mono text-sm text-red-400">
          {error}
        </p>
      )}

      <div className="mt-8">
        {loading ? (
          <p className="font-mono text-sm text-muted">loading…</p>
        ) : posts.length === 0 ? (
          <div className="terminal-card px-6 py-12 text-center text-muted">
            No posts yet. Hit <span className="font-mono text-accent">+ new</span> to write one.
          </div>
        ) : (
          <div className="terminal-card divide-y divide-border overflow-hidden">
            {posts.map((post) => (
              <div
                key={post.slug}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
              >
                <span
                  className={`rounded px-1.5 py-0.5 font-mono text-[11px] uppercase ${
                    post.status === 'published'
                      ? 'bg-accent/15 text-accent'
                      : 'bg-elevated text-muted'
                  }`}
                >
                  {post.status ?? 'draft'}
                </span>
                <button
                  onClick={() => openEdit(post.slug)}
                  className="min-w-0 flex-1 truncate text-left font-mono text-sm text-fg hover:text-accent"
                >
                  {post.title}
                </button>
                <span className="font-mono text-xs tabular-nums text-muted">
                  {formatDate(post.updatedAt ?? post.createdAt)}
                </span>
                <button
                  onClick={() => handleDelete(post.slug)}
                  disabled={busySlug === post.slug}
                  className="font-mono text-xs text-muted hover:text-red-400 disabled:opacity-50"
                >
                  {busySlug === post.slug ? '…' : 'delete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
